import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { ParametersNewTemplate } from "../models/ParametersNewTemplate";
import type { Template } from "../models/Template";

const moment = getMomentTz();

export interface GetTemplatesParams {
	assetId: number | string;
	searchText: string;
	showDownDay: boolean;
}

// React Query queryFn: throws on error, returns already-parsed + transformed rows.
export const fetchTemplates = async ({
	assetId,
	searchText,
	showDownDay,
}: GetTemplatesParams): Promise<Template[]> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "AssetId", value: assetId !== "A" && assetId },
		{ name: "SearchText", value: searchText },
		{ name: "ShowDownDay", value: !!showDownDay },
	];

	const resp = await apiService.callV2("[MMS].[GetTemplates]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error fetching templates");

	const rows = get(resp, "data.tables[0].rows", []) as any[];
	return rows.map((row, index) => ({
		...row,
		id: row.TemplateId,
		GridId: index,
		hasDownTime: row.DowntimeClassCode === "D",
		hasOpenWorkOrders: row.HasOpenWorkOrders == 1,
		PeriodShifts: row.PeriodShifts / 2,
		WarningShifts: row.WarningShifts / 2,
		ExpectedShifts: row.ExpectedShifts / 2,
		ScheduleStart: row.ScheduleStart
			? moment.utc(row.ScheduleStart).toDate()
			: null,
	})) as Template[];
};

// Returns shifts-per-day (ShiftCodes.length) for the asset, or null.
export const getShiftParameters = async (
	assetId: number | string,
): Promise<number | null> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "AssetId", value: assetId }];

	const resp = await apiService.callV2(
		"[MES].[GetShiftParameters]",
		parameters,
	);
	if (!resp.ok) throw new Error(resp.message || "Error fetching shift parameters");

	return get(resp, "data.tables[0].rows[0].ShiftCodes.length", null);
};

export const createPMOrder = async (TemplateId: number): Promise<void> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "TemplateId", value: TemplateId }];

	const resp = await apiService.callV2("[MMS].[CreatePMOrder]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error opening work order");
};

export const deleteTemplate = async (templateId: number): Promise<void> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
	];

	const resp = await apiService.callV2("[MMS].[DeleteTemplate]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error deleting template");
};

export interface UpsertTemplateResult {
	TemplateId: number | null;
	// Name of the work order opened by saving a new template (e.g. PM000205), null when none.
	OpenedWorkOrderName: string | null;
}

export const upsertTemplate = async (
	pNT: ParametersNewTemplate,
): Promise<UpsertTemplateResult> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "TemplateId", value: pNT.TemplateId });
	parameters.push({ name: "AssetId", value: pNT.AssetId });
	parameters.push({ name: "TypeCode", value: pNT.TypeCode });
	parameters.push({ name: "Title", value: pNT.Title });
	parameters.push({ name: "CraftId", value: pNT.CraftId });
	parameters.push({
		name: "PeriodShifts",
		value: (pNT.selectedPeriodDays ?? 0) * pNT.shiftsPerDay,
	});
	parameters.push({
		name: "WarningShifts",
		value: (pNT.selectedWarningDays ?? 0) * pNT.shiftsPerDay,
	});
	parameters.push({
		name: "ExpectedShifts",
		value: (pNT.selectedExpectedDays ?? 0) * pNT.shiftsPerDay,
	});
	parameters.push({
		name: "DownHours",
		value: Number.parseInt(pNT.DownHours as any),
	});
	parameters.push({
		name: "DowntimeClassCode",
		value: pNT.DowntimeClassCode ? "D" : "N",
	});
	parameters.push({ name: "IsNextDownDay", value: !!pNT.IsNextDownDay });
	parameters.push({
		name: "OpenFirstWorkOrder",
		value: !!pNT.OpenFirstWorkOrder,
	});
	parameters.push({
		name: "PlannedHours",
		value: Number.parseInt(pNT.PlannedHours as any),
	});
	parameters.push({ name: "Description", value: pNT.Description });
	parameters.push({
		name: "NumberOfWorkers",
		value: Number.parseInt(pNT.NumberOfWorkers as any),
	});
	parameters.push({
		name: "SchedulingPolicyCode",
		value: pNT.SchedulingPolicyCode,
	});
	parameters.push({
		name: "ScheduleStart",
		value: moment.utc(pNT.ScheduleStart).toISOString(),
	});
	parameters.push({
		name: "CronExpression",
		value: pNT.CronExpression as string,
	});

	const resp = await apiService.callV2("[MMS].[UpsertTemplate]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error saving template");

	return get(resp, "data.tables[0].rows[0]", null) as UpsertTemplateResult;
};

// ATTACHMENTS
export const getTemplateAttachments = async (TemplateId: string | number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TemplateId", value: Number.parseInt(TemplateId as string) },
	];
	return await apiService.callV2("[MMS].[GetTemplateAttachments]", parameters);
};

export const linkTemplateAttachments = async (
	templateId: number,
	attachments: {
		UploadFileId: number;
		FileName: string;
		IsAssetResource: boolean;
	}[],
) => {
	const apiService = new MESApiService();
	const formattedAttachments = attachments
		.map(
			(att) =>
				`[${att.UploadFileId}]:[${att.FileName}]:${
					att.IsAssetResource ? 1 : 0
				}`,
		)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "Attachments", value: formattedAttachments },
	];
	return await apiService.callV2("[MMS].[LinkTemplateAttachments]", parameters);
};

export const unLinkTemplateAttachments = async (
	templateId: number,
	attachments: { UploadFileId: number; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedAttachments = attachments
		.map((att) => `[${att.UploadFileId}]:${att.IsAssetResource ? 1 : 0}`)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "TemplateId", value: templateId },
		{ name: "Attachments", value: formattedAttachments },
	];
	return await apiService.callV2(
		"[MMS].[UnlinkTemplateAttachments]",
		parameters,
	);
};

export const getAssetResources = async (
	AssetId: string | number | undefined,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});

	const resp = await apiService.callV2("[MMS].[GetAssetResources]", parameters);
	return resp;
};

export const getTemplateResources = async (TemplateId: number | string) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "TemplateId", value: TemplateId });

	const resp = await apiService.callV2(
		"[MMS].[GetTemplateResources]",
		parameters,
	);
	return resp;
};

// Add attachment to asset (for linking template attachments to asset)
// Uses the same SP as asset-details: [MMS].[LinkAssetAttachments]
export const addLinkAssetAttachments = async (
	AssetId: number | string,
	UploadFileId: number | number[],
	Comments: string | null = null,
) => {
	const apiService = new MESApiService();

	// Convert array to comma-separated string if needed
	const uploadFileIdsCSV = Array.isArray(UploadFileId)
		? UploadFileId.join(",")
		: String(UploadFileId);

	const parameters: spParameter[] = [];

	parameters.push({ name: "AssetId", value: Number(AssetId) });
	parameters.push({ name: "UploadFileIdsCSV", value: uploadFileIdsCSV });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2("[MMS].[LinkAssetAttachments]", parameters);
	return resp;
};
